import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Printer, Download, TrendingUp, Home, XCircle, CheckCircle2, MoonStar } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { CHANNEL_COLORS } from "@/lib/channels";
import { toast } from "sonner";
import { useCompany } from "@/context/CompanyContext";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const money = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(v || 0);

const stateBadge = {
  "CHECKED-OUT": "bg-slate-200 text-slate-700 border-slate-300",
  "CHECKED-IN": "bg-emerald-100 text-emerald-800 border-emerald-200",
  UPCOMING: "bg-blue-100 text-blue-800 border-blue-200",
  VACANT: "bg-slate-100 text-slate-500 border-slate-200",
  CANCELLED: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function MonthlyReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { company } = useCompany();

  const years = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear() + 1; y >= 2023; y--) arr.push(y);
    return arr;
  }, [now]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const r = await api.get("/stats/monthly", { params: { year, month } });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load monthly report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, [year, month, company]);

  const printReport = () => window.print();

  const exportCSV = () => {
    if (!data?.properties?.length) return toast.info("Nothing to export");
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [
      ["Property", "State", "Status", "Check-in", "Check-out", "Channel", "Booked By", "Guests", "Nights", "Payout", "Payment"].join(","),
    ];
    for (const p of data.properties) {
      for (const b of p.bookings) {
        lines.push([
          p.property, b.state, b.status, b.check_in, b.check_out, b.ota_or_direct,
          b.booked_by, b.guests, b.nights, b.host_payout, b.payment_status,
        ].map(esc).join(","));
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `irentals-monthly-${year}-${String(month).padStart(2,"0")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 lg:space-y-8 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap print:hidden">
        <div>
          <div className="label-eyebrow">Monthly Progress</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">
            Progress Report
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Per-property occupancy, booking status and cancellations for the selected month.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="label-eyebrow">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="rounded-sm border-slate-300 h-10 w-36" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="label-eyebrow">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="rounded-sm border-slate-300 h-10 w-24" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={exportCSV} className="rounded-sm border-slate-300 h-10" data-testid="monthly-export-csv">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button onClick={printReport} className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white h-10" data-testid="monthly-print">
            <Printer className="w-4 h-4 mr-2" /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Report title (also prints) */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 print:border-none">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">{company}</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 mt-1">
              Progress Report — {data?.month_name || MONTHS[month - 1]} {year}
            </h2>
            <div className="text-xs text-slate-500 mt-1">
              Updated as of {new Date().toLocaleDateString("en-GB", { dateStyle: "long" })}
            </div>
          </div>
          {data && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
              <MetricInline label="Portfolio Occupancy" value={`${data.totals.portfolio_occupancy_pct}%`} icon={TrendingUp} />
              <MetricInline label="Booked Nights" value={data.totals.booked_nights} icon={Home} />
              <MetricInline label="Vacant Nights" value={data.totals.vacant_nights ?? 0} icon={MoonStar} tone="muted" />
              <MetricInline label="Cancellations" value={data.totals.cancellations} icon={XCircle} tone={data.totals.cancellations > 0 ? "danger" : "muted"} />
            </div>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="p-8 text-sm text-slate-500">Loading…</div>
      ) : data.properties.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-500">
          No bookings recorded for {MONTHS[month - 1]} {year}.
        </div>
      ) : (
        data.properties.map((p) => (
          <PropertyCard key={p.property} property={p} navigate={navigate} />
        ))
      )}

      <div className="text-xs text-slate-500 mt-8 italic print:mt-4 print:hidden">
        Notes: Occupancy is computed as booked nights (excluding cancellations & vacant) divided by days in the month.
        CHECKED-OUT = guest has left. CHECKED-IN = guest currently staying. UPCOMING = future arrival. VACANT = property marked vacant. CANCELLED = voided reservation.
      </div>
    </div>
  );
}

function MetricInline({ label, value, icon: Icon, tone }) {
  const toneCls = tone === "danger" ? "text-rose-700" : tone === "muted" ? "text-slate-500" : "text-blue-900";
  return (
    <div>
      <div className="flex items-center gap-1.5 label-eyebrow">
        <Icon className={`w-3.5 h-3.5 ${toneCls}`} />
        <span>{label}</span>
      </div>
      <div className={`font-heading text-2xl font-semibold tracking-tight mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function PropertyCard({ property: p, navigate }) {
  const pieData = [
    { name: "Booked", value: p.booked_nights, color: "#1E3A8A" },
    { name: "Vacant", value: p.vacant_nights, color: "#E2E8F0" },
  ];

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-6 space-y-6 print:break-inside-avoid"
      data-testid={`monthly-property-${p.property}`}
    >
      {/* Header row */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow">Property</div>
          <h3 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 mt-1 truncate">{p.property}</h3>
          <div className="text-xs text-slate-500 mt-1">{p.days_in_month} days in month · Host payout {money(p.payout)}</div>

          {/* Progress bar */}
          <div className="mt-5 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="label-eyebrow">Occupancy</span>
              <span className="font-heading text-base font-semibold text-blue-900">{p.occupancy_pct}%</span>
            </div>
            <div className="w-full h-3 bg-slate-100 rounded-sm overflow-hidden border border-slate-200">
              <div
                className="h-full bg-blue-900 transition-all"
                style={{ width: `${p.occupancy_pct}%` }}
                data-testid={`progress-${p.property}`}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Booked {p.booked_nights} nights</span>
              <span>Vacant {p.vacant_nights} nights</span>
            </div>
          </div>

          {/* Status counters — Vacant shows total vacant NIGHTS (not booking count) */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-5">
            <StateStat label="Checked-out" value={p.counts["CHECKED-OUT"]} tone="slate" />
            <StateStat label="Checked-in" value={p.counts["CHECKED-IN"]} tone="emerald" icon={CheckCircle2} />
            <StateStat label="Upcoming" value={p.counts.UPCOMING} tone="blue" />
            <StateStat label="Vacant nights" value={p.vacant_nights ?? 0} tone="slate" />
            <StateStat label="Cancelled" value={p.counts.CANCELLED} tone="rose" />
          </div>
        </div>

        {/* Pie chart */}
        <div className="w-full lg:w-56 h-40 lg:h-44 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                innerRadius={35}
                outerRadius={65}
                paddingAngle={2}
                startAngle={90}
                endAngle={-270}
              >
                {pieData.map((s, i) => <Cell key={i} fill={s.color} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Booking list for this property */}
      {p.bookings.length > 0 && (
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-100 border-y border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">State</th>
                <th className="text-left px-3 py-2.5 font-semibold">Check-in</th>
                <th className="text-left px-3 py-2.5 font-semibold">Check-out</th>
                <th className="text-left px-3 py-2.5 font-semibold">Channel</th>
                <th className="text-left px-3 py-2.5 font-semibold">Booked By</th>
                <th className="text-right px-3 py-2.5 font-semibold">Guests</th>
                <th className="text-right px-3 py-2.5 font-semibold">Nights</th>
                <th className="text-right px-3 py-2.5 font-semibold">Payout</th>
                <th className="text-left px-3 py-2.5 font-semibold">Payment</th>
              </tr>
            </thead>
            <tbody>
              {p.bookings.map((b) => (
                <tr
                  key={b.id}
                  onClick={() => navigate(`/bookings/${b.id}`)}
                  className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors"
                  data-testid={`monthly-row-${b.id}`}
                >
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${stateBadge[b.state] || "bg-slate-100"}`}>
                      {b.state}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{b.check_in || "—"}</td>
                  <td className="px-3 py-2.5">{b.check_out || "—"}</td>
                  <td className="px-3 py-2.5">
                    {b.ota_or_direct ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-semibold text-white"
                        style={{ background: CHANNEL_COLORS[b.ota_or_direct] || "#64748B" }}
                      >
                        {b.ota_or_direct}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5">{b.booked_by || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{b.guests || 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{b.nights || 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-blue-900">{money(b.host_payout)}</td>
                  <td className="px-3 py-2.5"><StatusBadge value={b.payment_status} type="payment" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StateStat({ label, value, tone }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`border rounded-sm p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="font-heading text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}
